import { useEffect } from "react";
import { MAP, hypot, isEditableTarget, isInteractiveTarget, midpoint, normalizeWheelDelta, screenToBoard, wheelZoomFactor } from "../utils/mapCamera";
import { POINTER } from "../utils/pointer";
import { useCamera } from "../components/investigation/CameraProvider";

const ZOOM_CURSOR_MS = 160;

export const useCameraGestures = (viewportRef) => {
  const { x, y, scale, panBy, setView, zoomAt, spaceHeldRef, pointerModeRef, setPointerMode } = useCamera();

  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return undefined;

    const pointers = new Map();
    const drag = {
      pending: false,
      active: false,
      didPan: false,
      pointerId: null,
      lastX: 0,
      lastY: 0,
      startX: 0,
      startY: 0,
    };
    const pinch = {
      active: false,
      startDistance: 1,
      startScale: 1,
      boardX: 0,
      boardY: 0,
    };
    const safari = {
      active: false,
      startScale: 1,
      boardX: 0,
      boardY: 0,
    };
    const wheel = {
      raf: 0,
      zoomDelta: 0,
      px: 0,
      py: 0,
      zooming: false,
    };
    let zoomCursorTimer = 0;

    const viewportPoint = (clientX, clientY) => {
      const rect = node.getBoundingClientRect();
      return { px: clientX - rect.left, py: clientY - rect.top };
    };

    const idlePointer = () => POINTER.GRAB;

    const capturePointer = (pointerId) => {
      try {
        if (node.setPointerCapture) node.setPointerCapture(pointerId);
      } catch {
        /* ignore missing native pointers */
      }
    };

    const releasePointer = (pointerId) => {
      try {
        if (node.hasPointerCapture && node.hasPointerCapture(pointerId)) {
          node.releasePointerCapture(pointerId);
        }
      } catch {
        /* ignore missing native pointers */
      }
    };

    const showZoomCursor = (deltaY) => {
      setPointerMode(deltaY < 0 ? POINTER.ZOOM_IN : POINTER.ZOOM_OUT);
      window.clearTimeout(zoomCursorTimer);
      zoomCursorTimer = window.setTimeout(() => {
        if (pointerModeRef.current === POINTER.GRABBING) return;
        setPointerMode(idlePointer());
      }, ZOOM_CURSOR_MS);
    };

    const endDrag = (pointerId) => {
      const wasActive = drag.active;
      const didPan = drag.didPan;
      if (drag.pointerId === pointerId || pointerId == null) {
        drag.pending = false;
        drag.active = false;
        drag.didPan = false;
        drag.pointerId = null;
        node.classList.remove("is-dragging");
        if (wasActive && pointerModeRef.current === POINTER.GRABBING) {
          setPointerMode(idlePointer());
        }
      }
      if (didPan) {
        const suppress = (event) => {
          event.preventDefault();
          event.stopPropagation();
          node.removeEventListener("click", suppress, true);
        };
        node.addEventListener("click", suppress, true);
        window.setTimeout(() => node.removeEventListener("click", suppress, true), 0);
      }
    };

    const beginPinch = () => {
      const [a, b] = [...pointers.values()];
      if (!a || !b) return;
      const mid = midpoint(a, b);
      const { px, py } = viewportPoint(mid.x, mid.y);
      const board = screenToBoard(px, py, x.get(), y.get(), scale.get());
      drag.pending = false;
      drag.active = false;
      node.classList.remove("is-dragging");
      pinch.active = true;
      pinch.startDistance = Math.max(hypot(a, b), 1);
      pinch.startScale = scale.get();
      pinch.boardX = board.x;
      pinch.boardY = board.y;
      node.classList.add("is-zooming");
      setPointerMode(POINTER.ZOOM_IN);
    };

    const flushWheel = () => {
      wheel.raf = 0;
      if (!wheel.zooming) return;
      zoomAt(wheel.px, wheel.py, scale.get() * wheelZoomFactor(wheel.zoomDelta));
      showZoomCursor(wheel.zoomDelta);
      wheel.zoomDelta = 0;
      wheel.zooming = false;
    };

    const onWheel = (event) => {
      if (isEditableTarget(event.target)) return;
      if (safari.active) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      const { px, py } = viewportPoint(event.clientX, event.clientY);
      const { deltaY } = normalizeWheelDelta(event);
      wheel.zooming = true;
      wheel.zoomDelta += deltaY;
      wheel.px = px;
      wheel.py = py;
      if (!wheel.raf) wheel.raf = requestAnimationFrame(flushWheel);
    };

    const onPointerDown = (event) => {
      if (event.button !== 0 && event.button !== 1) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (pointers.size === 2) {
        beginPinch();
        return;
      }

      const space = spaceHeldRef.current;
      const middle = event.button === 1;
      if (middle) event.preventDefault();

      drag.pending = true;
      drag.active = false;
      drag.didPan = false;
      drag.pointerId = event.pointerId;
      drag.lastX = event.clientX;
      drag.lastY = event.clientY;
      drag.startX = event.clientX;
      drag.startY = event.clientY;

      const interactive = isInteractiveTarget(event.target);
      if (!interactive || space || middle) {
        drag.pending = false;
        drag.active = true;
        node.classList.add("is-dragging");
        setPointerMode(POINTER.GRABBING);
        capturePointer(event.pointerId);
      }
    };

    const onPointerMove = (event) => {
      if (pointers.has(event.pointerId)) {
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      }

      if (pinch.active && pointers.size >= 2) {
        const [a, b] = [...pointers.values()];
        const distance = Math.max(hypot(a, b), 1);
        const mid = midpoint(a, b);
        const { px, py } = viewportPoint(mid.x, mid.y);
        const nextScale = pinch.startScale * (distance / pinch.startDistance);
        setView(px - pinch.boardX * nextScale, py - pinch.boardY * nextScale, nextScale);
        setPointerMode(nextScale >= pinch.startScale ? POINTER.ZOOM_IN : POINTER.ZOOM_OUT);
        return;
      }

      if (drag.pending && event.pointerId === drag.pointerId) {
        const moved = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
        if (moved >= MAP.panThreshold) {
          drag.pending = false;
          drag.active = true;
          drag.didPan = true;
          node.classList.add("is-dragging");
          setPointerMode(POINTER.GRABBING);
          capturePointer(event.pointerId);
        } else {
          return;
        }
      }

      if (!drag.active || event.pointerId !== drag.pointerId) return;
      const dx = event.clientX - drag.lastX;
      const dy = event.clientY - drag.lastY;
      if (dx === 0 && dy === 0) return;
      drag.lastX = event.clientX;
      drag.lastY = event.clientY;
      drag.didPan = true;
      panBy(dx, dy);
    };

    const onPointerUp = (event) => {
      pointers.delete(event.pointerId);
      if (pinch.active && pointers.size < 2) {
        pinch.active = false;
        node.classList.remove("is-zooming");
        setPointerMode(idlePointer());
      }
      releasePointer(event.pointerId);
      endDrag(event.pointerId);
    };

    const onLostCapture = (event) => {
      pointers.delete(event.pointerId);
      endDrag(event.pointerId);
    };

    const onGestureStart = (event) => {
      event.preventDefault();
      safari.active = true;
      const { px, py } = viewportPoint(event.clientX, event.clientY);
      const board = screenToBoard(px, py, x.get(), y.get(), scale.get());
      safari.startScale = scale.get();
      safari.boardX = board.x;
      safari.boardY = board.y;
      node.classList.add("is-zooming");
    };

    const onGestureChange = (event) => {
      event.preventDefault();
      if (!safari.active) return;
      const { px, py } = viewportPoint(event.clientX, event.clientY);
      const nextScale = safari.startScale * event.scale;
      setView(px - safari.boardX * nextScale, py - safari.boardY * nextScale, nextScale);
      setPointerMode(event.scale >= 1 ? POINTER.ZOOM_IN : POINTER.ZOOM_OUT);
    };

    const onGestureEnd = (event) => {
      event.preventDefault();
      safari.active = false;
      node.classList.remove("is-zooming");
      if (pointerModeRef.current !== POINTER.GRABBING) setPointerMode(idlePointer());
    };

    const onContextMenu = (event) => {
      if (event.button === 1 || spaceHeldRef.current) event.preventDefault();
    };

    const onAuxClick = (event) => {
      if (event.button === 1) event.preventDefault();
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    node.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    node.addEventListener("lostpointercapture", onLostCapture);
    document.addEventListener("gesturestart", onGestureStart, { passive: false });
    document.addEventListener("gesturechange", onGestureChange, { passive: false });
    document.addEventListener("gestureend", onGestureEnd, { passive: false });
    node.addEventListener("contextmenu", onContextMenu);
    node.addEventListener("auxclick", onAuxClick);

    return () => {
      window.clearTimeout(zoomCursorTimer);
      if (wheel.raf) cancelAnimationFrame(wheel.raf);
      window.removeEventListener("wheel", onWheel);
      node.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      node.removeEventListener("lostpointercapture", onLostCapture);
      document.removeEventListener("gesturestart", onGestureStart);
      document.removeEventListener("gesturechange", onGestureChange);
      document.removeEventListener("gestureend", onGestureEnd);
      node.removeEventListener("contextmenu", onContextMenu);
      node.removeEventListener("auxclick", onAuxClick);
      node.classList.remove("is-dragging", "is-zooming");
    };
  }, [panBy, pointerModeRef, scale, setPointerMode, setView, spaceHeldRef, viewportRef, x, y, zoomAt]);
};
